import { useCallback, useMemo, useState } from 'react';

import { usePreferencesStore } from '~/store/preferences.store';

/**
 * Reconciles a saved order against the table's current columns: keeps the saved
 * positions for ids that still exist, then appends any new ids in their natural
 * order. Keeps the order safe as a table evolves — a column added later shows up
 * (at the end) rather than vanishing, and one removed leaves nothing dangling.
 */
function reconcileOrder(
  saved: readonly string[] | undefined,
  current: readonly string[],
): string[] {
  if (!saved || saved.length === 0) return [...current];
  const currentSet = new Set(current);
  const kept = saved.filter((id) => currentSet.has(id));
  const keptSet = new Set(kept);
  const appended = current.filter((id) => !keptSet.has(id));
  return [...kept, ...appended];
}

/**
 * Column order for one table. When `tableId` is given the order persists across
 * remounts and sessions via the preferences store (localStorage); otherwise it
 * lives in session-only component state. Either way the returned order is
 * reconciled against `columnIds` so it always matches the table's real columns.
 *
 * @param tableId Stable table id; `undefined` disables persistence.
 * @param columnIds The table's current column ids, in their default order.
 */
export function useTableColumnOrder(
  tableId: string | undefined,
  columnIds: string[],
): [string[], (order: string[]) => void] {
  const stored = usePreferencesStore((s) => (tableId ? s.tableColumnOrder[tableId] : undefined));
  const setStored = usePreferencesStore((s) => s.setTableColumnOrder);
  const [local, setLocal] = useState<string[]>([]);

  const order = useMemo(
    () => reconcileOrder(tableId ? stored : local, columnIds),
    [tableId, stored, local, columnIds],
  );

  const setOrder = useCallback(
    (next: string[]) => {
      if (tableId) setStored(tableId, next);
      else setLocal(next);
    },
    [tableId, setStored],
  );

  return [order, setOrder];
}
